import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { CheckCircle2, Loader2, XCircle, GitBranch, FileSearch, Database, CheckCheck } from 'lucide-react';
import { lazyLogger } from '../../services/lazyLogger';
// Unified UI Components
import { Heading, Text } from '../ui/Typography';
import { Card, CardContent } from '../ui/Card';
import { LoadingState } from '../Common/LoadingState';
import { cn } from '../../utils/cn';
import { spacing } from '../../utils/spacing';
interface AnalysisProgressIndicatorProps {
  projectId: string;
  onComplete?: () => void;
}

interface ImportSession {
  id: string;
  status: string;
  progress_percentage: number;
  current_step: string;
  files_analyzed: number;
  total_files: number;
  error_message?: string;
}

const PROGRESS_STEPS = [
  { key: 'fetching_tree', label: 'Scanning repository', icon: GitBranch },
  { key: 'analyzing_files', label: 'Analyzing code structure', icon: FileSearch },
  { key: 'saving_data', label: 'Extracting patterns', icon: Database },
  { key: 'completed', label: 'Analysis complete', icon: CheckCheck },
];

export function AnalysisProgressIndicator({ projectId, onComplete }: AnalysisProgressIndicatorProps) {
  const [session, setSession] = useState<ImportSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSession();

    const channel = supabase
      .channel(`import_session_${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'import_sessions',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (payload.new) {
            const updatedSession = payload.new as unknown as ImportSession;
            setSession(updatedSession);

            if (updatedSession.status === 'completed' && onComplete) {
              setTimeout(onComplete, 1500);
            }
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [projectId, onComplete]);

  const loadSession = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('import_sessions')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setSession(data as unknown as ImportSession);

        if (data.status === 'completed' && onComplete) {
          setTimeout(onComplete, 1500);
        }
      }
    } catch (error) {
      lazyLogger.error('Failed to load import session:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, onComplete]);

  if (loading) {
    return <LoadingState message="Loading analysis progress..." size="md" />;
  }

  if (!session) {
    return null;
  }

  const isComplete = session.status === 'completed';
  const isFailed = session.status === 'failed';
  const currentStepIndex = PROGRESS_STEPS.findIndex((s) => s.key === session.status);

  const statusClasses = {
    complete: {
      bg: 'bg-success/20',
      border: 'border-success',
      icon: 'text-success',
      text: 'text-success',
      textSecondary: 'text-success',
    },
    failed: {
      bg: 'bg-destructive/20',
      border: 'border-destructive',
      icon: 'text-destructive',
      text: 'text-destructive',
      textSecondary: 'text-destructive',
    },
    processing: {
      bg: 'bg-primary/20',
      border: 'border-primary',
      icon: 'text-primary',
      text: 'text-primary',
      textSecondary: 'text-primary',
    },
  };

  const classes = isComplete
    ? statusClasses.complete
    : isFailed
    ? statusClasses.failed
    : statusClasses.processing;

  return (
    <Card className="overflow-hidden">
      <div className={cn("border-b p-6", classes.bg, classes.border)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isComplete ? (
              <CheckCircle2 className={cn("w-6 h-6", classes.icon)} />
            ) : isFailed ? (
              <XCircle className={cn("w-6 h-6", classes.icon)} />
            ) : (
              <Loader2 className={cn("w-6 h-6 animate-spin", classes.icon)} />
            )}
            <div>
              <Heading level={3} className={classes.text}>
                {isComplete
                  ? 'Repository Analysis Complete'
                  : isFailed
                  ? 'Analysis Failed'
                  : 'Analyzing Your Repository'}
              </Heading>
              <Text size="sm" className={classes.textSecondary}>
                {session.current_step}
              </Text>
            </div>
          </div>

          {!isFailed && session.total_files > 0 && (
            <div className="text-right">
              <Heading level={2} className={classes.text}>
                {session.progress_percentage}%
              </Heading>
              <Text size="xs" className={classes.textSecondary}>
                {session.files_analyzed} / {session.total_files} files
              </Text>
            </div>
          )}
        </div>
      </div>

      {isFailed && session.error_message && (
        <div className="border-b bg-destructive/20 border-destructive p-6">
          <Text size="sm" className="text-destructive">
            <span className="font-medium">Error:</span> {session.error_message}
          </Text>
        </div>
      )}

      {!isFailed && (
        <>
          <CardContent style={{ padding: spacing.card.padding }}>
            <div className="space-y-4">
              {PROGRESS_STEPS.map((step, index) => {
                const isCurrentStep = index === currentStepIndex;
                const isCompleted = index < currentStepIndex || isComplete;
                const Icon = step.icon;

                return (
                  <div key={step.key} className="flex items-center gap-4">
                    <div
                      className={cn(
                        'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                        isCompleted
                          ? 'bg-success/20'
                          : isCurrentStep
                          ? 'bg-primary/20'
                          : 'bg-background-secondary'
                      )}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5 text-success" />
                      ) : isCurrentStep ? (
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      ) : (
                        <Icon className="w-5 h-5 text-text-tertiary" />
                      )}
                    </div>

                    <div className="flex-1">
                      <Text
                        weight={isCompleted || isCurrentStep ? 'medium' : 'normal'}
                        className={
                          isCompleted
                            ? 'text-success'
                            : isCurrentStep
                            ? 'text-primary'
                            : 'text-text-tertiary'
                        }
                      >
                        {step.label}
                      </Text>
                    </div>

                    {isCompleted && (
                      <Text size="xs" weight="medium" className="text-success">
                        Complete
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>

          <div className="bg-background-secondary p-6">
            <div className="w-full rounded-full h-2 overflow-hidden bg-border">
              <div
                className={cn(
                  "h-full transition-all duration-500",
                  isComplete ? "bg-success" : "bg-primary"
                )}
                style={{ width: `${session.progress_percentage}%` }}
              />
            </div>
          </div>
        </>
      )}

      {isComplete && (
        <div className="bg-success/20 p-6">
          <Text size="sm" className="text-success">
            Your repository has been analyzed successfully. We've detected your tech stack, architecture patterns,
            and coding standards. You can now proceed with intelligent planning.
          </Text>
        </div>
      )}
    </Card>
  );
}
